import React, { forwardRef } from "react";

import {
  buildProductImageSrcSet,
  normalizeProductImage,
} from "../../utils/productImages";

export const CATALOGUE_PRODUCT_IMAGE_SIZES =
  "(max-width: 575px) calc(50vw - 18px), " +
  "(max-width: 991px) calc(50vw - 28px), " +
  "(max-width: 1199px) calc(33.333vw - 32px), " +
  "min(23vw, 360px)";

export const PRODUCT_DETAIL_IMAGE_SIZES =
  "(max-width: 991px) 100vw, min(54vw, 880px)";

const ResponsiveImage = forwardRef(function ResponsiveImage(
  {
    image,
    fallback = "",
    alt = "",
    sizes,
    className,
    pictureClassName,
    loading = "lazy",
    decoding = "async",
    onError,
    ...imgProps
  },
  ref,
) {
  const normalized = normalizeProductImage(image, fallback);
  const avifSrcSet = buildProductImageSrcSet(normalized, "avif");
  const webpSrcSet = buildProductImageSrcSet(normalized, "webp");
  const fallbackUrl = normalized?.url || fallback;
  const responsiveSizes = avifSrcSet || webpSrcSet ? sizes : undefined;

  const handleError = (event) => {
    const img = event.currentTarget;
    const resolvedFallback = fallback
      ? new URL(fallback, img.ownerDocument.baseURI).href
      : "";

    if (
      resolvedFallback &&
      img.currentSrc !== resolvedFallback &&
      img.src !== resolvedFallback
    ) {
      img.parentElement
        ?.querySelectorAll("source")
        .forEach((source) => source.removeAttribute("srcset"));
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
      img.src = fallback;
      return;
    }

    onError?.(event);
  };

  return (
    <picture className={pictureClassName}>
      {avifSrcSet ? (
        <source
          type="image/avif"
          srcSet={avifSrcSet}
          sizes={responsiveSizes}
        />
      ) : null}

      {webpSrcSet ? (
        <source
          type="image/webp"
          srcSet={webpSrcSet}
          sizes={responsiveSizes}
        />
      ) : null}

      <img
        {...imgProps}
        ref={ref}
        src={fallbackUrl}
        srcSet={webpSrcSet || undefined}
        sizes={responsiveSizes}
        width={normalized?.width || undefined}
        height={normalized?.height || undefined}
        alt={alt || normalized?.alt || "Image"}
        className={className}
        loading={loading}
        decoding={decoding}
        onError={handleError}
      />
    </picture>
  );
});

export default ResponsiveImage;
